
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import AdminBrandId from "@/mixins/adminBrandId";
import ClientMixin from "@/mixins/clientMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ConfirmModalModes, UUID } from "@/data/enums";
import { BrandConfigKeys } from "@/data/constants";

// Types
import { IProvider } from "@/models/providers";
import { IAccount } from "@/models/accounts";
import { IBrand } from "@/models/brands";
import { IClient } from "@/models/clients";
import {
  IClientProviderData,
  IClientProviderMethods
} from "@/models/providers/client";
import { IFraudEvent } from "@/models/fraud";

@Component({
  computed: {
    ...mapState({
      client(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CLIENTS,
          scope: this.clientScope
        });
      },
      clientScope(state, getters) {
        return getters[`data/${DataModules.CLIENTS}/scope`](this.clientId);
      }
    })
  }
})
export default class ClientProvider
  extends Mixins(RequestPromises, AdminBrandId, ClientMixin)
  implements IProvider {
  // Props
  @Prop({ required: true })
  public readonly clientId!: IClient["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: ClientProvider) => {
      return vm.$router.replace({ name: "adminClients" }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public readonly client!: IClient;
  public readonly clientScope!: string;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = {};
  public hasRequestError = false;

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $clientData: this.providerData(),
        $clientMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.loadData();
    // Register fraud event listener
    this.$bus.$on(`new-fraud-event`, this.fraudEventListener);
  }

  public beforeDestroy() {
    // Remove fraud event listener
    this.$bus.$off(`new-fraud-event`, this.fraudEventListener);
    // Bin client data
    this.$store.dispatch("data/binSingle", {
      scope: this.clientScope,
      storeModule: DataModules.CLIENTS,
      vm: this
    });
  }

  // Provide

  @Provide("clientProvider")
  public providerData(): IClientProviderData {
    return {
      account: this.clientAccount,
      accountId: this.clientAccountId,
      brandId: this.clientBrandId,
      canAddAsReferral: this.canAddAsReferral,
      canDelete: this.canDelete,
      canImpersonateClient: this.canImpersonateClient,
      canImpersonateUser: this.canImpersonateUser,
      canMerge: this.canMerge,
      canTopUpAccountCredit: this.canTopUpAccountCredit,
      client: this.client,
      clientId: this.clientId,
      hasRequestError: this.hasRequestError,
      identifier: this.clientIdentifier,
      isImported: this.isImported,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      isStaged: this.isStaged,
      scope: this.clientScope
    };
  }

  @Provide("clientMethods")
  public get providerMethods(): IClientProviderMethods {
    return {
      deleteClient: this.deleteClient,
      impersonateClient: this.impersonateClient,
      impersonateUser: this.impersonateUser,
      initClientMerge: this.initClientMerge,
      linkReferrer: this.linkReferrer,
      openMergeClientModal: this.openMergeClientModal,
      reloadData: this.reloadData
    };
  }

  // Computed

  public get clientAccount(): IAccount {
    return _.get(this.client, "accounts[0]");
  }

  public get clientAccountId(): IAccount["id"] {
    return _.get(this.clientAccount, "id");
  }

  public get clientBrandId(): IBrand["id"] {
    return _.get(this.client, "brand_id");
  }

  public get clientIdentifier() {
    return this.getClientName({
      client: this.client,
      clientId: this.clientId,
      orEmail: true
    }) as string;
  }

  public get isStaged() {
    return !!_.get(this.client, "staged_import");
  }

  public get isImported() {
    return !this.isStaged && !!_.get(this.client, "import_id");
  }

  public get canImpersonateUser(): boolean {
    const perms = ["start_impersonation_session", "create_user_access_token"];
    if (!this.$userCan(...perms)) return false;
    if (!this.$store.getters.isUpmindContext) return false;
    return !!this.client?.upmind_org_user?.id;
  }

  public get canImpersonateClient(): boolean {
    const perms = ["start_impersonation_session", "create_client_access_token"];
    if (!this.$userCan(...perms)) return false;
    if (this.$_.get(this.client, "is_guest")) return false;
    if (this.canImpersonateUser) return false;
    return true;
  }

  public get canDelete(): boolean {
    return this.$userCan("delete_client");
  }

  public get canAddAsReferral(): boolean {
    if (_.get(this.client, "is_guest")) return false;
    return (
      this.$userCan("create_affiliate_referral") &&
      _.isNull(this.$_.get(this.client, "affiliate_referral", null))
    );
  }

  public get canTopUpAccountCredit() {
    return this.$store.getters[
      `data/${DataModules.CLIENTS}/canTopUpAccountCredit`
    ](this.client);
  }

  public get canMerge(): boolean {
    return this.$userCan("merge_account_client");
  }

  // Methods

  public loadData({ ignoreStored } = { ignoreStored: false }) {
    return this.getClient(ignoreStored);
  }

  public async getClient(ignoreStored = false): Promise<any> {
    try {
      const { brand_id } = await this.$store.dispatch(
        `data/${DataModules.CLIENTS}/getClientWithRelatedData`,
        {
          id: this.clientId,
          scope: this.clientScope,
          ignoreStored,
          vm: this
        }
      );

      if (
        brand_id !== this.brandId &&
        this.brandId !== UUID.ORG &&
        this.onNotFound
      )
        this.onNotFound(this);

      /** Get required config keys for the client's brand (eg.
       * UPMIND_AFFILIATES_ENABLED to determine whether or not to show affiliate
       * menu item) and ignore stored values if in multi-brand mode */

      await this.$store
        .dispatch("brand/getConfig", {
          brandId: brand_id,
          keys: [BrandConfigKeys.UPMIND_AFFILIATES_ENABLED],
          ignoreStored: this.brandId === UUID.ORG
        })
        .catch(err => err);
      this.hasRequestError = false;
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.getClient(true);
  }

  public fraudEventListener(event: IFraudEvent) {
    if (event.client_id === this.clientId) {
      this.reloadData();
    }
  }

  public impersonateClient() {
    if (!this.canImpersonateClient) return Promise.resolve();
    this.$set(this.isProcessing, "impersonation", true);
    return this.$store
      .dispatch("auth/admin/impersonateClient", this.clientId)
      .finally(() => {
        this.$delete(this.isProcessing, "impersonation");
      });
  }

  public impersonateUser() {
    if (!this.canImpersonateUser) return Promise.resolve();
    this.$set(this.isProcessing, "impersonation", true);
    return this.$store
      .dispatch("auth/admin/impersonateUser", this.client.upmind_org_user?.id)
      .finally(() => {
        this.$delete(this.isProcessing, "impersonation");
      });
  }

  public deleteClient() {
    this.$set(this.isProcessing, "delete", true);
    return this.$store.dispatch(`data/${DataModules.CLIENTS}/confirmDelete`, {
      clientId: this.clientId,
      onSuccess: () => {
        this.$router.push({ name: "adminClients" });
      },
      onClose: () => this.$set(this.isProcessing, "delete", false)
    });
  }

  public async linkReferrer() {
    try {
      this.$store.dispatch(
        `data/${DataModules.CLIENTS}/openSelectClientModal`,
        {
          props: {
            title: this.$t("_action.select_affiliate_referrer"),
            allowGuestClientOption: false,
            showGuestsToggle: false,
            manualFilters: {
              "filter[accounts.affiliate_account.id|neq]": "null",
              "filter[id|neq]": this.clientId
            },
            withParams: "accounts.affiliate_account"
          },
          events: {
            onSelect: async (client: IClient) => {
              const affiliateAccountId = _.get(
                _.find(
                  client.accounts,
                  clientAccount =>
                    _.get(
                      clientAccount,
                      "affiliate_account.referral_count",
                      0
                    ) >= 0
                ),
                "affiliate_account.id"
              );
              const affiliateAccount = await this.$store.dispatch(
                `data/${DataModules.AFFILIATES_REFERRALS}/linkReferrer`,
                {
                  clientId: this.clientId,
                  affiliateAccountId
                }
              );

              this.$router.push({
                name: "adminClientAffiliateOverview",
                params: {
                  clientId: _.get(
                    affiliateAccount,
                    "affiliate_account.account.client[0].id"
                  )
                }
              });

              this.$store.dispatch("toast/show", {
                message: this.$t(
                  "_sentence.client_successfully_added_as_referral"
                )
              });
            }
          }
        }
      );
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public openMergeClientModal() {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS}/openMergeClientModal`,
      {
        clientId: this.clientId
      }
    );
  }

  public async initClientMerge() {
    if (!this.canMerge) return;
    try {
      const modal = await this.$store.dispatch(
        `data/${DataModules.CLIENTS}/openSelectClientModal`,
        {
          props: {
            title: this.$t("_action.select_target_client"),
            brandId: this.clientBrandId,
            manualFilters: {
              "filter[id|neq]": this.clientId
            }
          },
          events: {
            onSelect: async (targetClient: IClient) => {
              modal.close();
              const target = this.getClientName({
                client: targetClient,
                clientId: targetClient.id,
                orEmail: true
              }) as string;
              const confirm = await this.$store.dispatch(
                "ui/open/confirmModal",
                {
                  config: {
                    props: {
                      autoClose: false,
                      mode: ConfirmModalModes.PIN,
                      type: "is-success",
                      title: this.$t("_action.merge_client_with_target", {
                        target
                      }),
                      confirmButtonText: this.$t("_action.merge_client_data"),
                      message: this.$t("_sentence.merge_client_data_confirm", {
                        source: this.clientIdentifier,
                        sourceCreatedDate: this.$moment
                          .utc(this.client.created_at)
                          .format(this.$t("_datetime.date.shorter") as string),
                        target: target,
                        targetCreatedDate: this.$moment
                          .utc(targetClient.created_at)
                          .format(this.$t("_datetime.date.shorter") as string)
                      })
                    },
                    events: {
                      confirmed: async () => {
                        await this.doClientMerge(
                          _.get(targetClient, "id"),
                          _.get(targetClient, "accounts[0].id")
                        );
                        confirm.close();
                      }
                    }
                  }
                }
              );
            }
          }
        }
      );
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async doClientMerge(
    targetClientId: IClient["id"],
    targetAccountId: IAccount["id"]
  ) {
    try {
      await this.$store.dispatch(`data/${DataModules.CLIENTS}/mergeClient`, {
        source_account_id: this.clientAccountId,
        source_client_id: this.clientId,
        target_account_id: targetAccountId,
        target_client_id: targetClientId
      });

      this.$buefy.toast.open({
        message: this.$t("_.client_data_merged") as string
      });

      this.initDeleteAfterMerge();

      this.$router
        .push({
          name: "adminClientOverview",
          params: { clientId: targetClientId }
        })
        .catch(err => err);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async initDeleteAfterMerge() {
    if (!this.canDelete) return;
    try {
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            mode: ConfirmModalModes.BUTTON,
            type: "is-danger",
            title: this.$t("_sentence.delete_client_record_qn", {
              client: this.clientIdentifier
            }),
            confirmButtonText: this.$t("_action.delete_record"),
            closeButtonText: this.$t("_action.keep_record"),
            message: this.$t("_sentence.delete_client_after_merge", {
              source: this.clientIdentifier
            })
          },
          events: {
            confirmed: async () => {
              await this.$store.dispatch(`data/${DataModules.CLIENTS}/remove`, {
                id: this.clientId
              });
              this.$buefy.toast.open({
                message: this.$t("_.client_deleted") as string
              });
              confirm.close();
            }
          }
        }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }
}
